import { useEffect, useMemo, useState } from 'react';
import { Stack, Button, Group } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { useTenants } from '../../data/useTenants';
import { TenantsTable } from './components/TenantsTable';
import { TenantsGrid } from './components/TenantsGrid';
import { TenantsList } from './components/TenantsList';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | 'active' | 'inactive' | 'large' | 'empty';

export default function TenantsPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching } = useTenants();
  const all = useMemo(() => data?.data ?? [], [data]);

  // Per-page UI prefs: view + page size persist across navigation/refresh.
  const [view, setView] = usePersistedState<ViewMode>('admin.tenants.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.tenants.pageSize', 25);

  // Transient UI state: re-derived each visit.
  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  // The input stays instant; the filter pass over the whole directory waits for
  // a pause in typing, because every keystroke otherwise re-derives five counts
  // and two option lists over the full table.
  const debouncedQuery = useDebounce(query, 250);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) ||
    (kpi !== null && kpi !== 'all') ||
    drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  // KPIs: counts driven by data, click toggles a quick filter.
  const counts = useMemo(() => ({
    all: all.length,
    active: all.filter((tenant) => tenant.is_active).length,
    inactive: all.filter((tenant) => !tenant.is_active).length,
    large: all.filter((tenant) => tenant.user_count >= 5).length,
    empty: all.filter((tenant) => tenant.user_count === 0).length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',      label: t('tenants.kpi.all'), value: counts.all,      tone: 'primary' },
    { id: 'active',   label: t('status.active'), value: counts.active,   tone: 'healthy' },
    // A tenant nobody can sign in to is a failure state, so the count reads red
    // rather than the grey of a merely neutral bucket.
    { id: 'inactive', label: t('status.inactive'), value: counts.inactive, tone: 'down' },
    { id: 'large',    label: t('tenants.kpi.large'), value: counts.large,    tone: 'pending' },
    { id: 'empty',    label: t('tenants.kpi.empty'), value: counts.empty,    tone: 'warning' }
  ];

  const kpiLabels: Record<KpiKey, string> = {
    all: t('tenants.kpi.all'),
    active: t('status.active'),
    inactive: t('status.inactive'),
    large: t('tenants.kpi.large'),
    empty: t('tenants.kpi.empty')
  };

  // Filter dimensions for the drawer: populated from current data so we
  // never offer countries/timezones that don't exist.
  const filterFields: FilterField[] = useMemo(() => {
    const countries = Array.from(new Set(all.map((tenant) => tenant.country_iso))).sort();
    const timezones = Array.from(new Set(all.map((tenant) => tenant.timezone))).sort();
    return [
      {
        key: 'country',
        label: t('tenants.filters.country'),
        placeholder: t('tenants.filters.countryPlaceholder'),
        options: countries.map((c) => ({ value: c, label: c }))
      },
      {
        key: 'timezone',
        label: t('tenants.filters.timezone'),
        placeholder: t('tenants.filters.timezonePlaceholder'),
        options: timezones.map((tz) => ({ value: tz, label: tz }))
      },
      {
        key: 'status',
        label: t('common.status'),
        placeholder: t('tenants.filters.statusPlaceholder'),
        options: [
          { value: 'active', label: t('status.active') },
          { value: 'inactive', label: t('status.inactive') }
        ]
      }
    ];
  }, [all, t]);

  // What is narrowing the list, one removable pill each. A count badge on the
  // Filters button says how many are on but never which, so a short list reads
  // as a small platform rather than as a filter somebody forgot.
  const pills: ActiveFilterPill[] = [];
  if (kpi && kpi !== 'all') pills.push({ id: 'kpi', field: t('filters.field.show'), value: kpiLabels[kpi] });
  if (query.trim()) pills.push({ id: 'search', field: t('common.search'), value: query.trim() });
  for (const field of filterFields) {
    const value = filters[field.key];
    if (!value) continue;
    const option = field.options.find((o) => o.value === value);
    pills.push({ id: field.key, field: field.label, value: option?.label ?? value });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'kpi') { setKpi('all'); return; }
    if (id === 'search') { setQuery(''); return; }
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  // Compose KPI → search → drawer filters.
  const filtered = useMemo(() => {
    let rows = all;

    if (kpi && kpi !== 'all') {
      rows = rows.filter((tenant) => {
        switch (kpi) {
          case 'active':   return tenant.is_active;
          case 'inactive': return !tenant.is_active;
          case 'large':    return tenant.user_count >= 5;
          case 'empty':    return tenant.user_count === 0;
          default:         return true;
        }
      });
    }

    if (filters.country) rows = rows.filter((tenant) => tenant.country_iso === filters.country);
    if (filters.timezone) rows = rows.filter((tenant) => tenant.timezone === filters.timezone);
    if (filters.status === 'active') rows = rows.filter((tenant) => tenant.is_active);
    if (filters.status === 'inactive') rows = rows.filter((tenant) => !tenant.is_active);

    const q = debouncedQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((tenant) =>
        tenant.name.toLowerCase().includes(q) ||
        tenant.slug.toLowerCase().includes(q) ||
        tenant.country_iso.toLowerCase().includes(q) ||
        // Operators paste a tenant id out of a log line; matching it here saves
        // them a trip through another tab to translate it into a name.
        tenant.id.toLowerCase().includes(q)
      );
    }

    return rows;
  }, [all, kpi, filters, debouncedQuery]);

  // Reset page when filters / search shrink the dataset past the current page.
  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // "Nothing here yet" and "nothing matches" are different answers and get
  // different copy: only one of them has a filter to clear.
  const platformIsEmpty = !error && all.length === 0;

  const pillRow = (
    <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('tenants.title')}
        description={t('tenants.description')}
        actions={
          // The design puts a "+ New tenant" button here. There is no create
          // endpoint: organisations come into being at sign-up. The control
          // keeps its place and says why it cannot act.
          <Tooltip label={t('tenants.actions.newUnavailable')}>
            <span style={{ display: 'inline-flex' }}>
              <Button size="sm" leftSection={<PlusIcon />} disabled>{t('tenants.actions.new')}</Button>
            </span>
          </Tooltip>
        }
      />
      <TableShell
        title={t('tenants.table.title')}
        kpis={
          <KpiFilterRow
            items={kpiItems}
            value={kpi}
            onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }}
          />
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('tenants.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer
              triggerLabel={t('common.filters')}
              title={t('filters.drawer.title')}
              description={t('filters.drawer.description')}
              labels={{ apply: t('action.apply'), cancel: t('action.cancel'), clear: t('filters.clearAll'), any: t('filters.any') }}
              fields={filterFields}
              values={filters}
              onApply={(next) => { setFilters(next); setPage(1); }}
            />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <Stack gap="md" align="center">
            {pillRow}
            <EmptyState
              title={platformIsEmpty ? t('tenants.emptyPlatform') : t('tenants.empty')}
              description={platformIsEmpty ? t('tenants.emptyPlatformHint') : t('tenants.emptyHint')}
              action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
            />
          </Stack>
        }
        error={
          error ? (
            <Alert color="error">
              <Stack gap="sm">
                <span>{t('tenants.loadError')}</span>
                <Group gap="sm">
                  <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
                    {t('action.retry')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
      >
        {/* The pills and the footer live inside the body rather than in the
            shell's own slots, because the design puts them hard against the
            table: the pills directly under the toolbar, the range and pager
            directly under the last row. */}
        <Stack gap="md">
          {pillRow}
          {view === 'table' && <TenantsTable records={paged} loading={isLoading} />}
          {view === 'cards' && <TenantsGrid records={paged} />}
          {view === 'list'  && <TenantsList records={paged} />}
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </Stack>
      </TableShell>
    </Stack>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <line x1="7" y1="2.5" x2="7" y2="11.5" />
      <line x1="2.5" y1="7" x2="11.5" y2="7" />
    </svg>
  );
}
